import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe, Code, Zap, Shield, Users, Star } from 'lucide-react';
import Layout from '../../layouts/Layout';
import CTA from '../../components/CTA';

const WebDevelopmentPage = () => {
    const technologies = [
        {
            category: "Frontend Technologies",
            items: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vue.js", "Angular"]
        },
        {
            category: "Backend Technologies", 
            items: ["Node.js", "Express.js", "PHP", "Laravel", "Python", "FastAPI"]
        },
        {
            category: "Database Solutions",
            items: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "Firebase"]
        },
        {
            category: "Cloud & DevOps",
            items: ["AWS", "Azure", "Docker", "Kubernetes", "CI/CD", "Monitoring"]
        }
    ];

    const features = [
        {
            icon: <Zap className="w-6 h-6" />,
            title: "Lightning Fast Performance",
            description: "Optimised for speed with advanced caching, CDN integration, and performance monitoring to ensure exceptional user experience."
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: "Enterprise Security",
            description: "Bank-level security with SSL certificates, data encryption, regular security audits, and compliance with UK data protection regulations."
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Scalable Architecture",
            description: "Built to grow with your business using microservices, cloud infrastructure, and modular design patterns that adapt to your needs."
        },
        {
            icon: <Code className="w-6 h-6" />,
            title: "Clean, Maintainable Code",
            description: "Following industry best practices with comprehensive documentation, automated testing, and code reviews for long-term sustainability."
        }
    ];

    const process = [
        {
            step: "01",
            title: "Discovery & Strategy",
            description: "We begin with a comprehensive analysis of your business requirements, target audience, and technical objectives to create a tailored development strategy."
        },
        {
            step: "02", 
            title: "Design & Architecture",
            description: "Our team designs the user experience and technical architecture, ensuring optimal performance, security, and scalability from the ground up."
        },
        {
            step: "03",
            title: "Development & Testing", 
            description: "Using agile methodologies, we build your application with regular testing, code reviews, and continuous integration to maintain quality."
        },
        {
            step: "04",
            title: "Deployment & Launch",
            description: "We handle the complete deployment process, including server setup, SSL configuration, and performance optimisation for a smooth launch."
        },
        {
            step: "05",
            title: "Ongoing Support",
            description: "Post-launch, we provide comprehensive maintenance, security updates, performance monitoring, and feature enhancements."
        }
    ];

    const benefits = [
        "Custom-built solutions tailored to your specific business needs",
        "Responsive design ensuring perfect functionality across all devices", 
        "SEO-optimised structure for improved search engine visibility",
        "Integration capabilities with existing systems and third-party services",
        "Comprehensive admin panels for easy content management",
        "Advanced analytics and reporting functionality",
        "24/7 monitoring and support with rapid response times",
        "Regular security updates and performance optimisation"
    ];

    const industries = [
        "Financial Services & FinTech",
        "Healthcare & Medical",
        "E-commerce & Retail", 
        "Education & Training",
        "Manufacturing & Industrial",
        "Professional Services",
        "Non-profit & Government",
        "Technology & SaaS"
    ];

    return (
        <Layout
            title="Web Development Services"
            description="Professional web development services in the UK. Custom React, Node.js, and PHP applications built for enterprise performance, security, and scalability."
            keywords="web development UK, React development, Node.js development, PHP development, custom web applications, enterprise web development"
            path="/services/web-development"
        >
            <div className="min-h-screen bg-white">
                {/* Hero Section */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                    <Globe className="w-4 h-4 mr-2" />
                                    Web Development
                                </div>
                                
                                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight">
                                    Custom Web Applications
                                    <span className="block text-slate-700 mt-2">Built for Success</span>
                                </h1>
                                
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    We create high-performance, scalable web applications using modern technologies like React, 
                                    Node.js, and PHP. Our enterprise-grade solutions are designed to drive business growth and 
                                    deliver exceptional user experiences.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link to="/contact" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800 transition-all hover:shadow-lg flex items-center justify-center">
                                        Start Your Project <ArrowRight className="ml-2 w-5 h-5" />
                                    </Link>
                                    <Link to="/portfolio" className="border-2 border-slate-200 text-slate-700 px-8 py-4 rounded-xl font-semibold hover:border-slate-300 hover:bg-slate-50 transition-all">
                                        View Our Portfolio
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="relative">
                                <div className="bg-white rounded-3xl p-8 shadow-2xl border border-slate-200">
                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Project Delivery</span>
                                            <span className="text-slate-900 font-semibold">On Time</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Performance Score</span>
                                            <span className="text-slate-900 font-semibold">99/100</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Security Rating</span>
                                            <span className="text-slate-900 font-semibold">A+</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-slate-600">Client Satisfaction</span>
                                            <div className="flex items-center">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Why Choose Our Web Development Services?</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We combine technical excellence with business acumen to deliver web applications that drive results
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <div key={index} className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-shadow">
                                    <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white mb-6">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Technologies Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Technology Stack</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                We use cutting-edge technologies to build robust, scalable web applications
                            </p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {technologies.map((tech, index) => (
                                <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-lg font-bold text-slate-900 mb-6">{tech.category}</h3>
                                    <div className="space-y-3">
                                        {tech.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className="flex items-center">
                                                <CheckCircle className="w-4 h-4 text-green-600 mr-3" />
                                                <span className="text-slate-700">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Process Section */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Our Development Process</h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                A proven methodology that ensures successful project delivery every time
                            </p>
                        </div>
                        
                        <div className="space-y-8">
                            {process.map((step, index) => (
                                <div key={index} className="flex items-start">
                                    <div className="flex-shrink-0 w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-lg mr-6">
                                        {step.step}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-slate-600 leading-relaxed">{step.description}</p>
                                    </div>
                                    {/* {index < process.length - 1 && (
                                        <ChevronRight className="w-6 h-6 text-slate-400 ml-6" />
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-24 bg-slate-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <h2 className="text-4xl font-bold text-slate-900 mb-8">What You Get</h2>
                                <p className="text-xl text-slate-600 mb-10 leading-relaxed">
                                    Our web development services deliver comprehensive solutions that exceed expectations 
                                    and provide lasting value for your business.
                                </p>
                                
                                <div className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <div key={index} className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-green-600 mr-4 flex-shrink-0 mt-0.5" />
                                            <span className="text-slate-700 text-lg">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">Industries We Serve</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {industries.map((industry, index) => (
                                        <div key={index} className="flex items-center p-3 bg-slate-50 rounded-lg">
                                            <div className="w-2 h-2 bg-slate-900 rounded-full mr-3"></div>
                                            <span className="text-slate-700 text-sm">{industry}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Stats */}
                <section className="py-24 bg-white">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="bg-slate-900 rounded-3xl p-12 text-white">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl font-bold mb-6">Performance That Speaks for Itself</h2>
                                <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                                    Our web applications consistently deliver exceptional performance and reliability
                                </p>
                            </div>
                            
                            <div className="grid md:grid-cols-4 gap-8 text-center">
                                <div>
                                    <div className="text-4xl font-bold mb-2">99.9%</div>
                                    <div className="text-slate-300">Uptime Guarantee</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">&lt;2s</div>
                                    <div className="text-slate-300">Average Load Time</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">A+</div>
                                    <div className="text-slate-300">Security Rating</div>
                                </div>
                                <div>
                                    <div className="text-4xl font-bold mb-2">24/7</div>
                                    <div className="text-slate-300">Support Available</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <CTA />
            </div>
        </Layout>
    );
};

export default WebDevelopmentPage;